/**
 * ClientSuggestionRow — one "did you mean" candidate in the client box.
 *
 * Story 28633: the candidates showed only name + branch, so two clients with
 * the same name in the same branch read as one client listed twice. The row
 * now carries the details ora's client search row shows, built by the SAME
 * function (`buildClientOptionSegments`, the header search's row) so the two
 * lists cannot disagree: city/state(/zip), branch, location, client type —
 * whatever the candidate carries (analyze_email's suggestions bring city,
 * state and branch).
 *
 * Branch is always shown here, as it was before: the AM is choosing between
 * candidates ora proposed, and branch is often the tie-breaker.
 *
 * Shared — no Office.js, no Remix import.
 */
import { buildClientOptionSegments } from "~/common/components/ClientQuicklinkDropdown";
import { toClientOption } from "~/common/workflows/clientSearchOption";
import { TYPE_BODY, TYPE_TITLE } from "~/common/workflows/paneTypography";
import type { ClientSearchResult } from "~/common/workflows/types/client";

/** "Salem, OR - Pacific Northwest" — the candidate's details line, or "". */
export function clientSuggestionDetails(result: ClientSearchResult): string {
  return buildClientOptionSegments(toClientOption(result), true)
    .map((segment) => segment.text)
    .join(" - ");
}

export interface ClientSuggestionRowProps {
  result: ClientSearchResult;
  onPick: (result: ClientSearchResult) => void;
  /** The last row of its list draws no bottom divider. */
  last?: boolean;
}

export function ClientSuggestionRow({ result, onPick, last = false }: ClientSuggestionRowProps) {
  const details = clientSuggestionDetails(result);
  return (
    <button
      type="button"
      onClick={() => onPick(result)}
      data-testid="client-suggestion-row"
      className={
        "flex w-full touch-target flex-col items-start gap-0.5 bg-base-white px-[10px] py-[7px] text-left hover:bg-primary-gray-light-50 " +
        (last ? "" : "border-b border-primary-gray-light-100")
      }
    >
      <span className={`${TYPE_TITLE} w-full min-w-0 truncate text-primary-gray-light-900`}>
        {result.clientName}
      </span>
      {details ? (
        <span className={`${TYPE_BODY} w-full min-w-0 truncate text-primary-gray-light-500`}>
          {details}
        </span>
      ) : null}
    </button>
  );
}

export default ClientSuggestionRow;
